import React, { useEffect } from "react";
import logo from "../assets/SVG/Logo_new.svg";
import img from "../assets/login_img.png";
import bg from "../assets/about3.png";
import { Link, NavLink, useNavigate } from "react-router-dom";
import GenericApi from "@/api/genericApi";
import { ApiRoutes } from "@/config/routes/ApiRoutes";
import { RoutesPathList } from "@/config/routes/Routes";
import { ApiResponse, ApiError } from "@/types/Api";
import {
  RegisterRequest,
  RegisterRequestSchema,
  AuthResponse,
} from "@/types/Auth";
import { ValidationErrors, FormError } from "@/types/ValidationError";
import CustomLogger from "@/utils/CustomLogger";
import { formatError } from "@/utils/formatError";
import { generateRegisterInputs } from "@/utils/generateInputs";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { AxiosError, isAxiosError } from "axios";
import { useForm } from "react-hook-form";
import FormField from "@/components/CustomForm/FormField";
import { isBitramp } from "@/utils/utils";
import bg_Bitramp from "../assets/SVG/bg_Bitramp.svg";
import logo_Bitramp from "../assets/SVG/logo_Bitramp.svg";
import img_Bitramp from "../assets/SVG/img_Bitramp.svg";
import MuiPhoneNumber from "mui-phone-number";

const Register: React.FC = () => {
  const navigate = useNavigate();

  const [bitrampPhone, setBitrampPhone] = React.useState<string>("");

  useEffect(() => {
    const themeClass = isBitramp() ? "theme-bitramp" : "theme-leonext";
    document.body.classList.add(themeClass);

    return () => {
      document.body.classList.remove("theme-bitramp");
    };
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm<RegisterRequest>({
    resolver: zodResolver(RegisterRequestSchema),
  });

  const [infoMessage, setInfoMessage] = React.useState<string | null>(null);

  const registerMutation = useMutation<
    ApiResponse<AuthResponse>,
    ApiError<ValidationErrors>,
    RegisterRequest
  >({
    mutationFn: (data) =>
      GenericApi.post<AuthResponse>(ApiRoutes.USERS, {
        ...data,
        demo_phonenumber: isBitramp() ? bitrampPhone : "",
        is_demo: isBitramp() ? true : false,
      }),
    onSuccess: async () => {
      setInfoMessage("Check your email for the verification link");
      navigate("/register-confirm");
    },
    onError: async ({ error }) => {
      CustomLogger.error(error);
      handleAxiosError(error);
    },
  });

  const handleAxiosError = (error: AxiosError<unknown> | null | Error) => {
    if (isAxiosError<FormError>(error)) {
      setError("root", {
        type: "manual",
        message:
          error.response?.data?.detail ?? "An error occurred while registering",
      });
      return;
    }

    if (isAxiosError<ValidationErrors>(error)) {
      setError("root", {
        type: "manual",
        message: formatError(error, "An error occurred while registering"),
      });
      return;
    }

    setError("root", {
      type: "manual",
      message: error?.message ?? "An error occurred while registering",
    });
  };

  const onSubmit = async (data: RegisterRequest) => {
    console.log("Register data:", data);
    CustomLogger.log(data);
    await registerMutation.mutateAsync(data);
  };

  const registerInputs = generateRegisterInputs();

  return isBitramp() ? (
    <div className="register p-0">
      <img className="register__bg__img_bitramp" src={bg_Bitramp} alt="bg" />
      <div className="register__left pl-[2rem]">
        <Link
          className="register__left__logo z-[99]"
          to={RoutesPathList.Landing}
        >
          <img src={logo_Bitramp} className="w-[7rem] h-[4rem]" alt="logo" />
        </Link>
        <div className="register__left__image">
          <img
            className="register__left__image--resize !h-[83%] pr-[2.3rem]"
            src={img_Bitramp}
            alt="img"
          />
        </div>
        <div className="register__left__text">Get Started Now</div>
      </div>
      <div className="register__container custom-scrollbar !overflow-y-auto h-[40rem] !rounded-[1.5625rem] !bg-[#262626] !border !border-[#262626] !shadow-none mr-[6rem]">
        <div className="register__container__top !text-white !text-[3rem]">
          <Link
            to={RoutesPathList.Landing}
            className="register__container__top__arrow"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="15"
              height="24"
              viewBox="0 0 15 24"
              fill="none"
            >
              <path
                d="M13 2L2.81394 11.2601C2.3775 11.6568 2.3775 12.3432 2.81393 12.7399L13 22"
                stroke="#fff"
                stroke-width="3"
                strokeLinecap="round"
              />
            </svg>
          </Link>

          <div>Sign Up</div>
        </div>
        <form
          className="register__container__fields !mt-[2rem] z-[999]"
          onSubmit={handleSubmit(onSubmit)}
        >
          {registerInputs.map(
            (input) => (
              // input.name === "terms" ? (
              //   <div
              //     key={input.name}
              //     className="flex flex-row-reverse justify-end items-center gap-2"
              //   >
              //     <FormField<RegisterRequest>
              //       type={input.type}
              //       placeholder={input.placeholder}
              //       name={input.name}
              //       valueAsNumber={input.valueAsNumber}
              //       register={register}
              //       error={errors[input.name as keyof RegisterRequest]}
              //       required={input.required}
              //       label={input.label}
              //       inputContainerStyle="flex justify-center"
              //       labelStyle="register__container__tos__text font-normal"
              //       inputStyle="register__container__fields__space self-start"
              //     />
              //   </div>
              // ) : (
              <div key={input.name} className="flex-c gap-2">
                <FormField<RegisterRequest>
                  type={input.type}
                  placeholder={input.placeholder}
                  name={input.name}
                  valueAsNumber={input.valueAsNumber}
                  register={register}
                  error={errors[input.name as keyof RegisterRequest]}
                  required={input.required}
                  label={input.label}
                  labelStyle="register__container__fields__title !text-[1.2rem] !text-white"
                  inputStyle="register__container__fields__space !bg-[#2B2B2B] !border !border-[#464646] !text-white placeholder:text-[1rem] focus:outline-none focus:ring-0 focus:border-none"
                />
              </div>
            )
            // )
          )}
          <div
            className={
              isBitramp() ? "page-wrapper is-bitramp-register" : "page-wrapper"
            }
          >
            <label
              htmlFor="bitrampPhone"
              className="register__container__fields__title !text-[1.2rem] !text-white"
            >
              Telephone number
              <span className=" ml-1">*</span>
            </label>
            <MuiPhoneNumber
              defaultCountry="gi"
              value={bitrampPhone}
              onChange={(value) => setBitrampPhone(value as string)}
              inputClass="register__container__fields__space !bg-[#2B2B2B] !border !border-[#464646] !text-white placeholder:text-[1rem] focus:outline-none focus:ring-0 focus:border-none w-full !py-[5px] !mt-2"
              sx={{
                "& .MuiInputBase-input": {
                  width: "15rem",
                  fontSize: "0.875rem !important",
                  color: isBitramp() ? "#fff !important" : "#000 !important",
                  paddingRight: "2rem !important",
                  ":focus": {
                    border: "none !important",
                    boxShadow: "none !important",
                    outline: "none !important",
                    color: isBitramp() ? "#fff !important" : "#000 !important",
                  },
                  ":hover": {
                    color: isBitramp() ? "#fff !important" : "#000 !important",
                  },
                },
                "& .MuiInputBase-input, & .MuiInputBase-input:focus, & .MuiInputBase-input:hover, & .MuiInputBase-input:active":
                  {
                    color: isBitramp() ? "#fff !important" : "#000 !important",
                  },
                ".MuiInputBase-root": {
                  width: "100%",
                  paddingY: "0.31rem",
                  color: isBitramp() ? "#fff !important" : "#000 !important",
                  "& input": {
                    color: isBitramp() ? "#fff !important" : "#000 !important",
                  },
                  "::before": {
                    borderBottom: "none !important",
                    display: "none !important",
                  },
                  "::after": {
                    borderBottom: "none !important",
                    display: "none !important",
                  },
                  ":hover::before": {
                    borderBottom: "none !important",
                  },
                  ":focus-within::after": {
                    borderBottom: "none !important",
                  },
                  ":hover:not(.Mui-disabled):before": {
                    borderBottom: "none !important",
                  },
                },
                "& .MuiInput-root::before": {
                  borderBottom: "none !important",
                  display: "none !important",
                },
                "& .MuiInput-root::after": {
                  borderBottom: "none !important",
                  display: "none !important",
                },
                "& .MuiInput-root:hover::before": {
                  borderBottom: "none !important",
                },
                "& .MuiInput-root.Mui-focused::after": {
                  borderBottom: "none !important",
                },
                ".css-5h82ro-MuiInputBase-root-MuiInput-root::before": {
                  borderBottom: "none !important",
                  display: "none !important",
                },
                ".css-5h82ro-MuiInputBase-root-MuiInput-root::after": {
                  borderBottom: "none !important",
                  display: "none !important",
                },
                ".css-5h82ro-MuiInputBase-root-MuiInput-root:hover:not(.Mui-disabled, .Mui-error):before":
                  {
                    borderBottom: "none !important",
                  },
                ".css-5h82ro-MuiInputBase-root-MuiInput-root.Mui-focused::after":
                  {
                    borderBottom: "none !important",
                  },
                ".css-53g0n7-MuiButtonBase-root-MuiIconButton-root": {
                  marginLeft: "1rem !important",
                  paddingLeft: "0.4rem !important",
                  width: "2rem !important",
                },
                ".css-1yrc8ca-MuiInputBase-input-MuiInput-input, .css-1yrc8ca-MuiInputBase-input-MuiInput-input:focus, .css-1yrc8ca-MuiInputBase-input-MuiInput-input:hover":
                  {
                    fontSize: "0.875rem !important",
                    color: isBitramp() ? "#fff !important" : "#000 !important",
                  },
                ".MuiPaper-root": {
                  backgroundColor: "#2B2B2B !important",
                  color: "#fff !important",
                },
                ".MuiMenuItem-root": {
                  color: "#fff !important",
                  "&:hover": {
                    backgroundColor: "#1a1a1a !important",
                  },
                },
                ".Mui-selected": {
                  backgroundColor: "#202a43 !important",
                },
              }}
            />
          </div>
          <div className="flex gap-2 !text-white">
            <input
              type="checkbox"
              required
              className="register__container__fields__space !bg-[#2b2b2b] !border !border-[#505050] focus:outline-none focus:ring-0 focus:border-none"
            />
            <div>
              I agree to the{" "}
              <NavLink to={"/tou"} className={"underline"}>
                terms and conditions
              </NavLink>
              *
            </div>
          </div>

          <button
            type="submit"
            className="Primary_button w-[7.3125rem] h-[2.6875rem]"
          >
            {registerMutation.isPending ? "Loading..." : "Sign up"}
          </button>

          {infoMessage ? (
            <span className="block text-green-400 pt-4 sm:inline">
              {infoMessage}
            </span>
          ) : null}
        </form>
        {registerMutation.isError ? (
          <span className="block text-red-500 pt-4 sm:inline">
            {errors.root?.message}
          </span>
        ) : null}
        <div className="flex items-center w-full mt-[1rem]">
          <div className="h-px flex-1 bg-[#393939]"></div>
          <span className="mx-4 text-white font-[500] text-[1rem]">Or</span>
          <div className="h-px flex-1 bg-[#393939]"></div>
        </div>
        <div className="register__container__switch !mt-[2.2rem]">
          <div className="register__container__switch__text !text-white font-[400]">
            Have an account?
          </div>
          <Link
            to={RoutesPathList.Login}
            className="register__container__switch__link !text-[#8AFEFD]"
          >
            Log In
          </Link>
        </div>
      </div>
    </div>
  ) : (
    <div className="register p-0">
      <img className="register__bg__img" src={bg} alt="bg" />
      <div className="register__left">
        <Link
          className="register__left__logo z-[99]"
          to={RoutesPathList.Landing}
        >
          <img src={logo} alt="logo" />
        </Link>
        <div className="register__left__image">
          <img className="register__left__image--resize" src={img} alt="img" />
        </div>
        <div className="register__left__text">Get Started Now</div>
      </div>
      <div className="register__container !overflow-y-auto h-[40rem]">
        <div className="register__container__top !text-[3rem]">
          <Link
            to={RoutesPathList.Landing}
            className="register__container__top__arrow"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="15"
              height="24"
              viewBox="0 0 15 24"
              fill="none"
            >
              <path
                d="M13 2L2.81394 11.2601C2.3775 11.6568 2.3775 12.3432 2.81393 12.7399L13 22"
                stroke="#05004E"
                stroke-width="3"
                strokeLinecap="round"
              />
            </svg>
          </Link>

          <div>Sign Up</div>
        </div>
        <form
          className="register__container__fields"
          onSubmit={handleSubmit(onSubmit)}
        >
          {registerInputs.map((input) =>
            input.name === "terms" ? (
              <div
                key={input.name}
                className="flex flex-row-reverse justify-end items-center gap-2"
              >
                <FormField<RegisterRequest>
                  type={input.type}
                  placeholder={input.placeholder}
                  name={input.name}
                  valueAsNumber={input.valueAsNumber}
                  register={register}
                  error={errors[input.name as keyof RegisterRequest]}
                  required={input.required}
                  label={input.label}
                  inputContainerStyle="flex justify-center"
                  labelStyle="register__container__tos__text font-normal"
                  inputStyle="register__container__fields__space self-start"
                />
              </div>
            ) : (
              <div key={input.name} className="flex-c gap-2">
                <FormField<RegisterRequest>
                  type={input.type}
                  placeholder={input.placeholder}
                  name={input.name}
                  valueAsNumber={input.valueAsNumber}
                  register={register}
                  error={errors[input.name as keyof RegisterRequest]}
                  required={input.required}
                  label={input.label}
                  labelStyle="register__container__fields__title !text-[1.2rem]"
                  inputStyle="register__container__fields__space Input placeholder:text-[1rem]"
                />
              </div>
            )
          )}
          <div className="flex gap-2">
            <input
              type="checkbox"
              required
              className="register__container__fields__space"
            />
            <div>
              I agree to the{" "}
              <NavLink to={"/tou"} className={"underline"}>
                terms and conditions
              </NavLink>
              *
            </div>
          </div>

          <button
            type="submit"
            className="Primary_button w-[7.3125rem] h-[2.6875rem]"
          >
            {registerMutation.isPending ? "Loading..." : "Sign up"}
          </button>

          {infoMessage ? (
            <span className="block text-green-400 pt-4 sm:inline">
              {infoMessage}
            </span>
          ) : null}
        </form>
        {registerMutation.isError ? (
          <span className="block text-red-500 pt-4 sm:inline">
            {errors.root?.message}
          </span>
        ) : null}

        <div className="register__container__switch">
          <div className="register__container__switch__text">
            Have an account?
          </div>
          <Link
            to={RoutesPathList.Login}
            className="register__container__switch__link"
          >
            Log In
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Register;
